"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RETRY_ALL_MAX = exports.BACKGROUND_JOB_PAGE_SIZE = exports.backgroundJobListQuerySchema = exports.BACKGROUND_JOB_REFUSALS = exports.BACKGROUND_JOB_STATE_LABELS = exports.BACKGROUND_JOB_STATES = exports.BACKGROUND_JOB_LABELS = exports.BACKGROUND_QUEUE_INFO = exports.BACKGROUND_QUEUES = void 0;
exports.backgroundJobLabel = backgroundJobLabel;
exports.describeSchedule = describeSchedule;
exports.jobActionRefusalText = jobActionRefusalText;
const zod_1 = require("zod");
const email_delivery_1 = require("./email-delivery");
// ADMIN Background work (Admin redesign batch 2, Option A; DECISIONS-LOG 2026-11-02 Q3): our own page over the
// BullMQ queues. Plain names for the queues and jobs, the schedule in words, and what the page reads. Bull Board
// stays behind "Technical view ↗" for anything deeper.
/** The queues, in the order the page lists them. The API's `QUEUE_NAMES` must be the same eight (a test checks). */
exports.BACKGROUND_QUEUES = ['email', 'sla', 'reminders', 'maintenance', 'import', 'download', 'jev', 'images'];
exports.BACKGROUND_QUEUE_INFO = {
    email: { label: 'Emails going out', hint: 'Every email the app sends, a few a minute so the mail server never blocks us.' },
    sla: { label: 'Deadlines and time limits', hint: 'Checks the time limits every 5 minutes and warns before one runs out.' },
    reminders: { label: 'Reminders and chasers', hint: 'Reminders, chasers and the morning emails.' },
    maintenance: { label: 'Nightly upkeep', hint: 'Invites, covers, retention and the monthly archive.' },
    import: { label: 'Imports', hint: 'The rows of an uploaded candidate file, one after another.' },
    download: { label: 'CV downloads', hint: 'CVs fetched from the links in an import, 5 at a time.' },
    jev: { label: 'Jev reading', hint: "Jev reading CVs against a job's profile." },
    images: { label: 'Advert images', hint: "Making a job's advert image." },
};
/** Plain names for the jobs. A job the map doesn't know shows its technical name, made readable. */
exports.BACKGROUND_JOB_LABELS = {
    'email.send': 'Send an email',
    'notification.deliver': 'Deliver a notification',
    'notification.escalate': 'Escalate something overdue',
    'notification.digest': 'Daily notification digest',
    'user.chase_invites': 'Chase invites not accepted',
    'delegation.activate': 'Start covers that begin today',
    'sla.evaluate': 'Check the time limits',
    'requisition.chase': 'Chase hire request approvals',
    'publishing.sweep': 'Open and close job postings',
    'scoring.rescore_job': "Re-rank a job's candidates",
    'scoring.read_cv_text': 'Read a CV',
    'scoring.reread_cv_text': 'Read a CV again',
    'email.release_scheduled': 'Send emails held for the next working day',
    'email.fail_stalled': 'Mark emails that stopped part-way as failed',
    'sorting.sweep': 'Sorting reminders',
    'pipeline.run_action': 'Run a bulk action',
    'assessment.sweep': 'Test reminders and expiry',
    'interview.sweep': 'Interview reminders',
    'scorecard.sweep': 'Scorecard reminders',
    'decision.sweep': 'Decision reminders',
    'hold.sweep': 'Holds ending soon',
    'shortlist.sweep': 'Shortlist reminders',
    'offer.sweep': 'Offers lapsing, and offer approvals waiting',
    'hire.sweep': 'Joining reminders, and seats emptying',
    'pool.sweep': 'Talent pool consent running out',
    'task.sweep': 'Follow-ups falling due',
    'inbox.poll': 'Read the inbound mailbox',
    'workspace.digest': "Recruiters' morning email",
    'report.breach_summary': 'Yesterday\'s time limit breaches',
    'report.morning_digest': 'Morning summary email',
    'report.send_scheduled': 'Send scheduled reports',
    'retention.sweep': 'Retention countdown and anonymising',
    'retention.archive': 'Monthly candidate archive',
    'import.run': 'Run an import',
    'import.download_cv': 'Download a CV',
    'jev.run_ask': 'Jev reads the CVs on a job',
    'job.make_image': "Make a job's advert image",
    'jev.read_new_for_profile': 'Jev reads new CVs',
    'scoring.rerank_profile': 'Re-rank a job by its profile',
    'icp.migrate_to_profile': 'Move jobs to profile ranking',
    'jev.read_pool_for_profile': 'Jev reads talent pool CVs',
};
function backgroundJobLabel(name) {
    const known = exports.BACKGROUND_JOB_LABELS[name];
    if (known)
        return known;
    const words = name.replace(/[._]+/g, ' ').trim();
    return words === '' ? name : `${words.charAt(0).toUpperCase()}${words.slice(1)}`;
}
/** 06-qol §11: "8:00 am", never 08:00. */
function clockTime(hour, minute) {
    const suffix = hour < 12 ? 'am' : 'pm';
    const twelve = hour % 12 === 0 ? 12 : hour % 12;
    return `${twelve}:${String(minute).padStart(2, '0')} ${suffix}`;
}
function ordinal(day) {
    const tens = day % 100;
    if (tens >= 11 && tens <= 13)
        return `${day}th`;
    switch (day % 10) {
        case 1:
            return `${day}st`;
        case 2:
            return `${day}nd`;
        case 3:
            return `${day}rd`;
        default:
            return `${day}th`;
    }
}
const NUMBER = /^\d+$/;
/**
 * A cron pattern in words: "Every 5 minutes", "Every hour, at 7 minutes past", "Daily at 8:00 am GMT+6",
 * "Monthly on the 1st at 6:00 am GMT+6". `zone` is the abbreviation shown after a time of day. A pattern of another
 * shape is shown as written, so nothing is ever described wrongly.
 */
function describeSchedule(pattern, zone) {
    const fields = pattern.trim().split(/\s+/);
    if (fields.length !== 5)
        return pattern;
    const [minute = '', hour = '', day = '', month = '', weekday = ''] = fields;
    if (month !== '*' || weekday !== '*')
        return pattern;
    const at = (h, m) => `${clockTime(Number(h), Number(m))}${zone === '' ? '' : ` ${zone}`}`;
    const every = /^\*\/(\d+)$/.exec(minute);
    if (every && hour === '*' && day === '*')
        return Number(every[1]) === 1 ? 'Every minute' : `Every ${Number(every[1])} minutes`;
    if (!NUMBER.test(minute))
        return pattern;
    if (hour === '*' && day === '*')
        return Number(minute) === 0 ? 'Every hour, on the hour' : `Every hour, at ${Number(minute)} minutes past`;
    // N-69's sweep (review R28, 2026-11-02): "5 8-20 * * *" runs hourly through the working day.
    const hours = /^(\d+)-(\d+)$/.exec(hour);
    if (hours && day === '*')
        return `Every hour from ${at(hours[1] ?? '', minute)} to ${at(hours[2] ?? '', minute)}`;
    if (!NUMBER.test(hour))
        return pattern;
    if (day === '*')
        return `Daily at ${at(hour, minute)}`;
    if (NUMBER.test(day))
        return `Monthly on the ${ordinal(Number(day))} at ${at(hour, minute)}`;
    return pattern;
}
// --- What the page reads ---------------------------------------------------------------------------------------
/** "Finished" are the jobs BullMQ still keeps (a day, at most 1,000 a queue); the event log has every outcome. */
exports.BACKGROUND_JOB_STATES = ['failed', 'waiting', 'active', 'delayed', 'completed'];
exports.BACKGROUND_JOB_STATE_LABELS = {
    failed: 'Failed',
    waiting: 'Waiting',
    active: 'Running',
    delayed: 'Later',
    completed: 'Finished',
};
exports.BACKGROUND_JOB_REFUSALS = ['job_gone', 'job_not_failed', 'job_part_off', 'redis_down'];
const BACKGROUND_REFUSAL_TEXT = {
    job_gone: 'That job is no longer there. It may have been retried or removed a moment ago.',
    job_not_failed: 'Only a failed job can be retried or removed.',
    job_part_off: 'The part of the app this job works for is switched off, so it would do nothing.',
    redis_down: "The queue store can't be reached right now. Try again in a minute.",
};
function isBackgroundRefusal(refusal) {
    return exports.BACKGROUND_JOB_REFUSALS.includes(refusal);
}
/** The refusal in words; for an email that held a sign-in link, with where to send a fresh one (as Email delivery says). */
function jobActionRefusalText(refusal, templateKey = null) {
    return isBackgroundRefusal(refusal) ? BACKGROUND_REFUSAL_TEXT[refusal] : (0, email_delivery_1.messageActionRefusalText)(refusal, templateKey);
}
exports.backgroundJobListQuerySchema = zod_1.z.object({
    state: zod_1.z.enum(exports.BACKGROUND_JOB_STATES).default('failed'),
    page: zod_1.z.coerce.number().int().positive().max(1000).default(1),
});
exports.BACKGROUND_JOB_PAGE_SIZE = 25;
/** At most this many go in one "Retry all"; the rest wait for the next click. */
exports.RETRY_ALL_MAX = 200;
//# sourceMappingURL=background-work.js.map